/**
 * Utility for building searchable channel list combining regular, local, and RSN channels
 */

import { denormalizeChannels } from "lib/helper/channel-interactive-tool-v2";

interface LocalRsnChannelsData {
  localChannels: any[];
  rsnChannels: any[];
}

/**
 * Builds a searchable channel list by combining:
 * 1. Regular channels from chlupV2 (denormalized if needed)
 * 2. Local channels from zipcode API
 * 3. RSN channels from zipcode API
 *
 * @param allChannels - All channels from chlupV2 (can be string[] or object[])
 * @param channelMap - Map of channel IDs to channel objects
 * @param localRsnChnlsData - Local and RSN channels from zipcode API
 * @returns Array of searchable channel objects with displayText property
 */
export const buildSearchableChannels = (allChannels?: any[] | string[], channelMap?: Record<string, any>, localRsnChnlsData?: LocalRsnChannelsData): any[] => {
  let channels: any[] = [];

  // Add regular channels from chlupV2
  if (allChannels && channelMap) {
    // Check if allChannels is already denormalized (array of objects) or normalized (array of strings)
    const firstChannel = allChannels[0];
    if (typeof firstChannel === "string") {
      // Denormalize: string[] -> ChannelCardV2[]
      channels = denormalizeChannels(allChannels as string[], channelMap);
    } else {
      // Already denormalized
      channels = allChannels as any[];
    }
  }

  // Add local/RSN channels with displayText for search
  if (localRsnChnlsData && (localRsnChnlsData.localChannels.length > 0 || localRsnChnlsData.rsnChannels.length > 0)) {
    // Transform local channels to include displayText
    const localChannelsForSearch = localRsnChnlsData.localChannels.map(ch => ({
      label: ch.network, // Use network as the label (e.g., "NBC", "FOX")
      displayText: ch.network, // Required for search component
      logo: ch.logo || "", // Logo path already transformed by zipcode component
      categoryId: "LOCAL",
      rawData: ch,
      isLocal: true,
      signaturePackages: [], // Required for clickability check
      genrePackages: [], // Required for clickability check
      hasIncludedPackages: true // Local channels are always included
    }));

    // Transform RSN channels to include displayText
    const rsnChannelsForSearch = localRsnChnlsData.rsnChannels.map(ch => ({
      label: ch.network, // Use network as the label (e.g., "FanDuel Sports Network Detroit")
      displayText: ch.network, // Required for search component
      logo: ch.logo || "", // Logo path already transformed by zipcode component
      categoryId: "RSN",
      rawData: ch,
      isRsn: true,
      signaturePackages: [], // Required for clickability check
      genrePackages: [], // Required for clickability check
      hasIncludedPackages: Boolean(ch.packageNames?.length > 0) // RSN channels check packageNames
    }));

    // Combine all channels
    channels = [...channels, ...localChannelsForSearch, ...rsnChannelsForSearch];
  }

  return channels;
};
